import { Button, Select, Space, Typography } from "antd"
import { useEffect, useState } from "react"
import type { PhysicalKind } from "../../types/physical"
import { physicalApi, physicalError } from "../../utils/physicalApi"
import { usePhysicalResource } from "./usePhysicalResource"
export function IdentityPicker({
  kind,
  parent,
  value,
  onChange,
  disabled,
  label,
}: {
  kind: PhysicalKind
  parent?: string | null
  value?: string | null
  onChange: (value: string | null) => void
  disabled?: boolean
  label: string
}) {
  const [search, setSearch] = useState("")
  const [query, setQuery] = useState("")
  const [page, setPage] = useState(0)
  useEffect(() => {
    const timer = setTimeout(() => {
      setQuery(search)
      setPage(0)
    }, 250)
    return () => clearTimeout(timer)
  }, [search])
  useEffect(() => setPage(0), [parent])
  const allowed = !disabled && (kind === "site" || !!parent)
  const list = usePhysicalResource(
    allowed ? `picker/${kind}/${parent}/${query}/${page}` : "",
    (signal) =>
      physicalApi.list(
        kind,
        {
          q: query,
          limit: 50,
          offset: page * 50,
          ...(kind === "asset"
            ? { physicalSiteUuid: parent! }
            : kind === "component"
              ? { physicalAssetUuid: parent! }
              : {}),
        },
        signal,
      ),
  )
  const selected = usePhysicalResource(value ? `picker-selected/${kind}/${value}` : "", (signal) =>
    physicalApi.get(kind, value!, signal),
  )
  const items = list.data?.items ?? []
  const options =
    selected.data && !items.some((x) => x.uuid === selected.data!.uuid)
      ? [selected.data, ...items]
      : items
  return (
    <Space direction="vertical" style={{ width: "100%" }} size={4}>
      <Select
        aria-label={label}
        placeholder={label}
        style={{ width: "100%" }}
        value={value ?? undefined}
        disabled={!allowed}
        allowClear
        showSearch
        filterOption={false}
        onSearch={setSearch}
        onChange={(v) => onChange(v ?? null)}
        loading={list.loading}
        notFoundContent={list.error ? "Chargement impossible" : "Aucun résultat"}
        options={options.map((x) => ({
          value: x.uuid,
          label: `${x.name}${x.code ? ` · ${x.code}` : ""}${x.archived ? " · Archivé" : ""}`,
          disabled: x.archived,
        }))}
        dropdownRender={(menu) => (
          <>
            {menu}
            <Space style={{ padding: 8 }}>
              <Button
                size="small"
                disabled={!page || list.loading}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => setPage((p) => p - 1)}
              >
                Précédents
              </Button>
              <Button
                size="small"
                disabled={!list.data || (page + 1) * 50 >= list.data.total || list.loading}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => setPage((p) => p + 1)}
              >
                Suivants
              </Button>
            </Space>
          </>
        )}
      />
      {list.error && (
        <Typography.Text type="danger">
          {physicalError(list.error)}{" "}
          <Button size="small" onClick={list.reload}>
            Réessayer
          </Button>
        </Typography.Text>
      )}
    </Space>
  )
}
